import { useQuery } from "@tanstack/react-query";
import { Link2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getSocialPlatform } from "@/lib/socialPlatforms";

export interface SocialLinkRow {
  id: string;
  platform: string;
  label: string;
  url: string;
  sort_order: number;
  is_active: boolean;
}

export interface ResolvedSocialLink {
  id: string;
  label: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
}

/**
 * Rendered until the saved links load (and if an admin removed every link),
 * so the footer never flashes empty. Mirrors the original hard-coded set.
 */
const FALLBACK_LINKS: SocialLinkRow[] = [
  { id: "fallback-youtube", platform: "youtube", label: "YouTube", url: "https://youtube.com/@podgopodcasts", sort_order: 0, is_active: true },
  { id: "fallback-twitter", platform: "twitter", label: "X", url: "https://x.com/podgoservices", sort_order: 1, is_active: true },
  { id: "fallback-instagram", platform: "instagram", label: "Instagram", url: "https://www.instagram.com/podgoservices", sort_order: 2, is_active: true },
  { id: "fallback-linkedin", platform: "linkedin", label: "LinkedIn", url: "https://www.linkedin.com/company/podgo-io/", sort_order: 3, is_active: true },
];

const resolve = (rows: SocialLinkRow[]): ResolvedSocialLink[] =>
  rows.map((row) => ({
    id: row.id,
    label: row.label,
    url: row.url,
    icon: getSocialPlatform(row.platform)?.icon ?? Link2,
  }));

/** Public social profiles, single source for the footer and homepage JSON-LD. */
export function useSocialLinks() {
  const { data, isLoading } = useQuery({
    queryKey: ["social-links", "active"],
    queryFn: async (): Promise<SocialLinkRow[]> => {
      // Table is not in the generated types file yet; cast like other
      // admin-managed content hooks (useNavMenu) do.
      const { data, error } = await (supabase as any)
        .from("social_links")
        .select("id, platform, label, url, sort_order, is_active")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as SocialLinkRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const rows = data && data.length > 0 ? data : FALLBACK_LINKS;
  return { links: resolve(rows), urls: rows.map((r) => r.url), isLoading };
}
